// /home/labdoo/zitech-ai/frontend/src/services/api.ts
import axios from 'axios';

const API_BASE_URL = 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Use 'authToken' consistently
api.interceptors.request.use(
  (config) => {
    // Use 'authToken' to match Login.tsx
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Token attached to:', config.url);
    } else {
      console.warn('⚠️ No auth token for:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data);
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Handle unauthorized - use 'authToken' consistently
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ============================================================================
// API Helpers
// ============================================================================

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

export const getCurrentUser = (): any => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

// ============================================================================
// User-Specific API Calls
// ============================================================================

export const userAPI = {
  getPredictions: () => api.get('/users/predictions/'),
  getPredictionStats: () => api.get('/users/prediction-stats/'),
  getLeaderboard: () => api.get('/users/leaderboard/'),
  getProfile: () => api.get('/users/profile/'),
  updateProfile: (data: any) => api.put('/users/profile/', data),
  getActivities: () => api.get('/users/activities/'),
  getSessions: () => api.get('/users/sessions/'),
  logoutAll: () => api.post('/users/logout-all/'),
};

// ============================================================================
// Prediction API Calls
// ============================================================================

export const predictionAPI = {
  predict: (data: any) => api.post('/predict/', data),
  predictFast: (data: any) => api.post('/predict-fast/', data),
  getHistory: () => api.get('/predictions/'),
  getDetail: (id: number) => api.get(`/predictions/${id}/`),
};

// ============================================================================
// Public API Calls
// ============================================================================

export const publicAPI = {
  getTeams: () => api.get('/teams/'),
  searchTeams: (query: string) => api.get(`/teams/search/?q=${encodeURIComponent(query)}`),
  getTeamDetail: (id: number) => api.get(`/teams/${id}/`),
  getTeamMatches: (id: number) => api.get(`/teams/${id}/matches/`),
  getMatches: (params?: any) => api.get('/matches/', { params }),
  getUpcomingMatches: () => api.get('/matches/upcoming/'),
  getLiveMatches: () => api.get('/matches/live/'),
  getMatchDetail: (id: number) => api.get(`/matches/${id}/`),
  getStats: () => api.get('/stats/'),
  getLeaderboard: () => api.get('/leaderboard/'),
  getHealth: () => api.get('/health/'),
};